/* fh-cart.js v2026-08-17-variant-truth
 *
 * Forever Hungry — shared cart interactions.
 *
 * Loaded once from layout/theme.liquid (defer); runs on every page. Handles:
 *   - Cart drawer open/close and item mutations (qty, remove) with subtotal + free-ship recalc
 *   - Cart pop-up modal (opens via "Complete the look" trigger; summary strip synced from drawer)
 *   - Size picker (nested modal above the pop-up; captures size before adding to cart)
 *   - REAL /cart/add.js POST when the size-picker payload carries Shopify variant data
 *     (PDP flow); falls back to client-side dummy insert for card ATC and modal upsells
 *     (Phase 4 wires those to real /cart/add.js once cards become {{ product }}-driven).
 *
 * Cart drawer + modal + picker DOM is rendered from snippets in layout/theme.liquid
 * AFTER <main> closes, so this script MUST be deferred. Anything that tries to touch
 * #drawer-subtotal / #cart-modal / #size-picker before DOMContentLoaded will null-ref.
 */
(function () {
  'use strict';

  // Free-shipping bar removed 2026-07-03 — FREE_SHIP_THRESHOLD constant + all
  // ship-bar / progress-bar / remaining-amount updates dropped with it.
  var CART_ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var CART_ADD_URL    = CART_ROOT + 'cart/add.js';
  var CART_URL        = CART_ROOT + 'cart.js';
  var CART_CHANGE_URL = CART_ROOT + 'cart/change.js';
  // 2026-10-27 (fhic): add-on lines — never counted as bag pieces. Shipping Protection is drawn as the drawer's
  // opt-in checkbox (assets/fhic-cart.js), not as a bag row, but still counts in the totals.
  var FHIC_ADDON = { 'shipping-protection': 1, 'onward-package-protection': 1, 'priority-processing': 1 };

  function fmt(n) { return '$' + n.toFixed(2); }

  // HTML-escape user-controlled strings before inserting into innerHTML. Product
  // titles / variant option values come from Shopify (usually safe) but a
  // renamed product with `<` or `&` could break the drawer HTML or (in extreme
  // cases) inject markup. Cheap defense.
  function esc(s) {
    if (s == null) return '';
    return String(s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  // Complete-the-look upsell items — parsed from a server-baked JSON blob in
  // fh-cart-modal.liquid (`<script type="application/json" data-fh-upsells>`).
  // The blob carries real Shopify variant IDs + option positions so the "+ Add"
  // flow does a real /cart/add.js POST — not a dummy line insert. If the blob
  // is missing (e.g. the snippet failed to render for some reason), UPSELL_ITEMS
  // stays empty and renderUpsells() bails silently.
  var UPSELL_ITEMS = [];
  function loadUpsellItems() {
    var el = document.querySelector('script[data-fh-upsells]');
    if (!el) return;
    try {
      var parsed = JSON.parse(el.textContent);
      if (Array.isArray(parsed)) UPSELL_ITEMS = parsed;
    } catch (e) {
      console.warn('[fh-cart] upsells JSON parse failed', e);
    }
  }

  ready(function () {
    // Parse server-baked upsell data first — before any modal interaction.
    loadUpsellItems();

    /* ===== A11y helpers — focus management + focus trap + scroll-lock ref-count =====
       Modal-like surfaces (drawer, cart modal, size picker, mobile nav) share the
       same needs: on open, move focus into the surface and lock body scroll; on
       close, restore focus to the trigger and unlock body (only when no other
       surface is still open). Tab must cycle within the open surface so screen
       readers + keyboard users don't fall through into the underlying page. */
    var _scrollLockCount = 0;
    var _lastOpener = [];  // Stack of elements to return focus to on close.
    function lockBody() {
      if (_scrollLockCount === 0) document.body.style.overflow = 'hidden';
      _scrollLockCount++;
    }
    function unlockBody() {
      _scrollLockCount = Math.max(0, _scrollLockCount - 1);
      if (_scrollLockCount === 0) document.body.style.overflow = '';
    }
    var FOCUSABLE_SELECTOR = [
      'a[href]', 'button:not([disabled])', 'input:not([disabled]):not([type="hidden"])',
      'select:not([disabled])', 'textarea:not([disabled])', '[tabindex]:not([tabindex="-1"])'
    ].join(', ');
    function getFocusables(container) {
      if (!container) return [];
      return Array.prototype.slice.call(container.querySelectorAll(FOCUSABLE_SELECTOR))
        .filter(function (el) {
          // Skip elements that are hidden via CSS (offsetParent === null for
          // display:none / hidden attribute).
          return el.offsetParent !== null || el === document.activeElement;
        });
    }
    function moveFocusInto(container) {
      var focusables = getFocusables(container);
      if (focusables.length) focusables[0].focus();
      else if (container) container.focus();
    }
    function trapTab(container, e) {
      if (e.key !== 'Tab' || !container) return;
      var focusables = getFocusables(container);
      if (!focusables.length) { e.preventDefault(); return; }
      var first = focusables[0];
      var last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault(); last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault(); first.focus();
      }
    }
    function returnFocus() {
      var el = _lastOpener.pop();
      if (el && typeof el.focus === 'function') {
        // Some triggers (esp. links) may have already been removed; try/catch.
        try { el.focus(); } catch (_) {}
      }
    }

    /* ===== Drawer open/close (called from anywhere) ===== */
    var drawerEl = document.getElementById('drawer');
    var overlayEl = document.getElementById('overlay');
    function openDrawer() {
      // 2026-10-27 (fhic): an in-drawer upsell add re-opens the already-open drawer; without this guard each add took
      // another scroll lock that closing the drawer never released.
      if (drawerEl && drawerEl.classList.contains('is-open')) { setTimeout(function () { moveFocusInto(drawerEl); }, 40); return; }
      if (document.activeElement) _lastOpener.push(document.activeElement);
      if (drawerEl) drawerEl.classList.add('is-open');
      if (overlayEl) overlayEl.classList.add('is-open');
      lockBody();
      // Wait a frame for the transition to start so focus doesn't fight it.
      setTimeout(function () { moveFocusInto(drawerEl); }, 40);
    }
    function closeDrawer() {
      if (drawerEl) drawerEl.classList.remove('is-open');
      if (overlayEl) overlayEl.classList.remove('is-open');
      unlockBody();
      returnFocus();
    }
    window.openFhDrawer = openDrawer;
    window.closeFhDrawer = closeDrawer;

    // Header cart button opens; overlay + close button close. Moved off inline
    // onclick handlers (CSP-hostile + logic duplicated across three files).
    var cartOpenBtn = document.getElementById('fh-cart-open');
    var drawerCloseBtn = document.getElementById('drawer-close');
    if (cartOpenBtn) cartOpenBtn.addEventListener('click', openDrawer);
    if (drawerCloseBtn) drawerCloseBtn.addEventListener('click', closeDrawer);
    if (overlayEl) overlayEl.addEventListener('click', closeDrawer);

    // ===== Smart sticky header =====
    // The header stays pinned to the top, hides while scrolling down, and
    // returns as soon as the shopper scrolls back up. Keep it visible at the
    // very top and while the mobile navigation drawer is open.
    var fhHeader = document.querySelector('.fh-header');
    if (fhHeader) {
      var lastScrollY = window.pageYOffset || document.documentElement.scrollTop || 0;
      var headerTicking = false;
      var headerRevealThreshold = 8;

      function updateHeaderOnScroll() {
        var currentY = window.pageYOffset || document.documentElement.scrollTop || 0;
        var delta = currentY - lastScrollY;

        if (currentY <= 4 || Math.abs(delta) < headerRevealThreshold) {
          if (currentY <= 4) fhHeader.classList.remove('fh-header--hidden');
          headerTicking = false;
          lastScrollY = currentY;
          return;
        }

        if (delta > 0) {
          fhHeader.classList.add('fh-header--hidden');
        } else {
          fhHeader.classList.remove('fh-header--hidden');
        }

        lastScrollY = currentY;
        headerTicking = false;
      }

      window.addEventListener('scroll', function () {
        if (!headerTicking) {
          window.requestAnimationFrame(updateHeaderOnScroll);
          headerTicking = true;
        }
      }, { passive: true });
    }

    // ===== Mobile navigation drawer (hamburger) =====
    var mobileNav = document.getElementById('mobile-nav');
    var mobileNavOverlay = document.getElementById('mobile-nav-overlay');
    var navToggle = document.getElementById('fh-nav-toggle');
    var mobileNavClose = document.getElementById('mobile-nav-close');
    function openMobileNav() {
      if (document.activeElement) _lastOpener.push(document.activeElement);
      if (mobileNav) {
        mobileNav.hidden = false;
        // Force reflow so the transform transition fires from the -100% start.
        void mobileNav.offsetWidth;
        mobileNav.classList.add('is-open');
      }
      if (mobileNavOverlay) mobileNavOverlay.classList.add('is-open');
      if (navToggle) navToggle.setAttribute('aria-expanded', 'true');
      lockBody();
      setTimeout(function () { moveFocusInto(mobileNav); }, 40);
    }
    function closeMobileNav() {
      if (mobileNav) mobileNav.classList.remove('is-open');
      if (mobileNavOverlay) mobileNavOverlay.classList.remove('is-open');
      if (navToggle) navToggle.setAttribute('aria-expanded', 'false');
      unlockBody();
      returnFocus();
      // Hide after transition so it doesn't interfere with tab-order on desktop.
      setTimeout(function () {
        if (mobileNav && !mobileNav.classList.contains('is-open')) mobileNav.hidden = true;
      }, 240);
    }
    if (navToggle) navToggle.addEventListener('click', openMobileNav);
    if (mobileNavClose) mobileNavClose.addEventListener('click', closeMobileNav);
    if (mobileNavOverlay) mobileNavOverlay.addEventListener('click', closeMobileNav);
    // Any link click closes the nav so navigating away doesn't leave it hovering.
    if (mobileNav) {
      mobileNav.querySelectorAll('a').forEach(function (a) {
        a.addEventListener('click', closeMobileNav);
      });
    }

    // Keyboard nav for any open surface — top-most surface owns Tab + ESC.
    // ESC closes; Tab is trapped (cycles within the surface) so keyboard users
    // + screen readers don't fall through into the underlying page.
    document.addEventListener('keydown', function (e) {
      var picker = document.getElementById('size-picker');
      var modal = document.getElementById('cart-modal');
      // Determine the top-most open surface for both ESC + Tab-trap.
      var topSurface = null;
      if (picker && picker.classList.contains('is-open')) topSurface = picker;
      else if (modal && modal.classList.contains('is-open')) topSurface = modal;
      else if (drawerEl && drawerEl.classList.contains('is-open')) topSurface = drawerEl;
      else if (mobileNav && mobileNav.classList.contains('is-open')) topSurface = mobileNav;

      if (e.key === 'Tab' && topSurface) {
        trapTab(topSurface, e);
        return;
      }
      if (e.key !== 'Escape') return;
      if (topSurface === picker) { closeSizePicker(); return; }
      if (topSurface === modal) { closeCartModal(); return; }
      if (topSurface === drawerEl) { closeDrawer(); return; }
      if (topSurface === mobileNav) { closeMobileNav(); }
    });

    // ===== recalcDrawer: subtotal + free-ship progress + modal-field sync =====
    function recalcDrawer() {
      var subtotal = 0, count = 0;
      document.querySelectorAll('#drawer-items .fh-drawer__item').forEach(function (item) {
        var qtyEl = item.querySelector('.fh-drawer__qty');
        var inputEl = item.querySelector('input');
        if (!qtyEl || !inputEl) return;
        var price = parseFloat(qtyEl.dataset.price || 0);
        var qty = parseInt(inputEl.value, 10) || 0;
        var lineTotal = price * qty;
        subtotal += lineTotal;
        if (item.getAttribute('data-addon') !== '1') count += qty;
        var lineTotalEl = item.querySelector('.line-total');
        if (lineTotalEl) lineTotalEl.textContent = fmt(lineTotal);
      });
      // 2026-10-27 (fhic): opt-in Shipping Protection is a real cart line that is not drawn as a bag row, so add it
      // back into the totals from the checkbox row's state.
      var fhicProt = document.getElementById('fhic-protect');
      if (fhicProt && fhicProt.getAttribute('data-on') === '1') subtotal += parseFloat(fhicProt.getAttribute('data-price-dollars') || 0) || 0;
      var sub = document.getElementById('drawer-subtotal');
      var tot = document.getElementById('drawer-total');
      if (sub) sub.textContent = fmt(subtotal);
      if (tot) tot.textContent = fmt(subtotal);

      var drawerTitle = document.getElementById('drawer-title');
      if (drawerTitle) drawerTitle.textContent = 'Your bag (' + count + ')';
      var headerCartCount = document.getElementById('fh-cart-count');
      if (headerCartCount) headerCartCount.textContent = count;

      // Keep the cart-modal (Complete-the-look surface) subtotal + count + row
      // fields in agreement with the drawer whether the shopper edited qty in
      // the drawer or added an upsell inside the modal.
      var modalSub = document.getElementById('cart-modal-subtotal');
      if (modalSub) modalSub.textContent = fmt(subtotal);
      var modalSumSub = document.getElementById('cart-modal-summary-subtotal');
      if (modalSumSub) modalSumSub.textContent = fmt(subtotal);
      var modalSumCount = document.getElementById('cart-modal-summary-count');
      if (modalSumCount) modalSumCount.textContent = count + (count === 1 ? ' item' : ' items');
    }
    window.recalcDrawer = recalcDrawer;

    /* ===== Cart pop-up modal (Complete the look upsell surface) ===== */
    var cartModal = document.getElementById('cart-modal');
    var cartModalOverlay = document.getElementById('cart-modal-overlay');
    var cartModalClose = document.getElementById('cart-modal-close');
    var cartModalThumbs = document.getElementById('cart-modal-summary-thumbs');
    var cartModalUpsellList = document.getElementById('cart-modal-upsell-list');
    var drawerSeeMore = document.getElementById('drawer-see-more');

    // Rebuild the compact thumbnail strip from the current drawer items so the
    // modal head reflects what's actually in the cart.
    function populateCartModalSummary() {
      if (!cartModalThumbs) return;
      var items = document.querySelectorAll('#drawer-items .fh-drawer__item');
      var thumbHtml = '';
      items.forEach(function (item) {
        var img = item.querySelector('.fh-drawer__item__img img');
        if (img && img.src) {
          thumbHtml += '<img src="' + esc(img.src) + '" alt="" loading="lazy">';
        }
      });
      cartModalThumbs.innerHTML = thumbHtml;
    }

    // Render the upsell carousel from the UPSELL_ITEMS array. Filters out any
    // items already in the drawer (compared by title) so we don't upsell what
    // the shopper already added.
    function renderUpsells() {
      if (!cartModalUpsellList) return;
      var inCart = {};
      document.querySelectorAll('#drawer-items .fh-drawer__item__title').forEach(function (el) {
        inCart[el.textContent.trim().toLowerCase()] = true;
      });
      var visible = UPSELL_ITEMS.filter(function (u) {
        return !inCart[u.title.toLowerCase()];
      });
      cartModalUpsellList.innerHTML = visible.map(function (u) {
        return ''
          + '<div class="fh-cart-modal__upsell-item" data-upsell-id="' + esc(u.id) + '">'
          +   '<div class="fh-cart-modal__upsell-item__img"><img src="' + esc(u.img) + '" alt="" loading="lazy"></div>'
          +   '<div class="fh-cart-modal__upsell-item__meta">'
          +     '<div class="fh-cart-modal__upsell-item__title">' + esc(u.title) + '</div>'
          +     '<div class="fh-cart-modal__upsell-item__price">' + fmt(u.price) + '</div>'
          +   '</div>'
          +   '<button type="button" class="fh-cart-modal__upsell-add" data-upsell-id="' + esc(u.id) + '" aria-label="Add ' + esc(u.title) + ' to bag">+ Add</button>'
          + '</div>';
      }).join('');
    }

    function openCartModal() {
      if (document.activeElement) _lastOpener.push(document.activeElement);
      populateCartModalSummary();
      renderUpsells();
      if (cartModalOverlay) cartModalOverlay.classList.add('is-open');
      if (cartModal) {
        cartModal.classList.add('is-open');
        cartModal.setAttribute('aria-hidden', 'false');
      }
      lockBody();
      setTimeout(function () { moveFocusInto(cartModal); }, 40);
    }
    function closeCartModal() {
      if (cartModalOverlay) cartModalOverlay.classList.remove('is-open');
      if (cartModal) {
        cartModal.classList.remove('is-open');
        cartModal.setAttribute('aria-hidden', 'true');
      }
      unlockBody();
      returnFocus();
    }
    window.openCartModal = openCartModal;
    window.closeCartModal = closeCartModal;

    if (drawerSeeMore) drawerSeeMore.addEventListener('click', openCartModal);
    if (cartModalClose) cartModalClose.addEventListener('click', closeCartModal);
    if (cartModalOverlay) cartModalOverlay.addEventListener('click', closeCartModal);

    // "+ Add" on an upsell card → open size picker with the upsell product.
    // Passes the full variants array + colorOptionPosition + sizeOptionPosition
    // through so resolveVariantId() finds a real Shopify variant ID and the
    // size-picker commit does a real /cart/add.js POST.
    if (cartModalUpsellList) {
      cartModalUpsellList.addEventListener('click', function (e) {
        var addBtn = e.target.closest('.fh-cart-modal__upsell-add');
        if (!addBtn) return;
        var id = addBtn.dataset.upsellId;
        var item = null;
        for (var i = 0; i < UPSELL_ITEMS.length; i++) {
          if (UPSELL_ITEMS[i].id === id) { item = UPSELL_ITEMS[i]; break; }
        }
        if (!item) return;

        // Build the size list — prefer server-provided sizes; fall back to
        // deriving from available variants; last-resort S/M/L/XL.
        var sizes = (item.sizes && item.sizes.length) ? item.sizes : [];
        if (!sizes.length && item.variants && item.sizeOptionPosition) {
          var seen = {};
          item.variants.forEach(function (v) {
            var s = v['option' + item.sizeOptionPosition];
            if (s && !seen[s]) { seen[s] = true; sizes.push(s); }
          });
        }
        if (!sizes.length) sizes = ['S', 'M', 'L', 'XL'];

        openSizePicker({
          title: item.title,
          price: item.price,
          img: item.img,
          color: item.color,
          size: sizes[0],
          productHandle: item.handle,
          variants: item.variants,
          colorOptionPosition: item.colorOptionPosition,
          sizeOptionPosition: item.sizeOptionPosition
        }, null, sizes);
      });
    }

    function bindDrawerItem(item) {
      var up = item.querySelector('.qty-up');
      var down = item.querySelector('.qty-down');
      var remove = item.querySelector('.fh-drawer__remove');
      var input = item.querySelector('input');
      var lineKey = item.dataset.key;
      // Real Shopify cart lines carry a hash-format key (e.g. "48b6d2ef…:abc123");
      // dummy lines (card ATC preview + upsell path pre-Phase 4) use "line-<ts>"
      // and the demo seed uses "line-demo". Any key that ISN'T "line-*" is a real
      // Shopify line that needs /cart/change.js sync — skipping the sync was the
      // bug where "remove then add" resurrected the removed item because Shopify
      // still had it.
      var isRealCartLine = !!lineKey && lineKey.indexOf('line-') !== 0;

      function localMutate(newQty) {
        if (newQty === 0) item.remove();
        else if (input) input.value = newQty;
        recalcDrawer();
      }

      function syncQty(newQty) {
        if (!isRealCartLine || !lineKey) { localMutate(newQty); return; }
        // Optimistic UI — reflect the change immediately, reconcile from /cart.js
        // once the server confirms (or roll back on failure).
        var prevValue = input ? input.value : '1';
        if (newQty === 0) item.style.opacity = '0.4';
        else if (input) input.value = newQty;
        recalcDrawer();

        fetch(CART_CHANGE_URL, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ id: lineKey, quantity: newQty })
        })
          .then(function (r) { return r.ok ? r.json() : Promise.reject(r); })
          .then(function () { return refreshDrawerFromShopifyCart(); })
          .catch(function (err) {
            console.warn('[fh-cart] change failed — rolling back', err);
            item.style.opacity = '';
            if (input) input.value = prevValue;
            recalcDrawer();
          });
      }

      if (up) up.addEventListener('click', function () {
        syncQty((parseInt(input.value, 10) || 0) + 1);
      });
      if (down) down.addEventListener('click', function () {
        syncQty(Math.max(0, (parseInt(input.value, 10) || 0) - 1));
      });
      if (remove) remove.addEventListener('click', function () {
        syncQty(0);
      });
    }
    document.querySelectorAll('#drawer-items .fh-drawer__item').forEach(bindDrawerItem);
    recalcDrawer();

    /* ===== Real Shopify cart sync ===== */
    // Refreshes the drawer contents from /cart.js after a real add. Rebuilds
    // #drawer-items HTML from the returned cart.items and rebinds handlers.
    function refreshDrawerFromShopifyCart() {
      return fetch(CART_URL, { credentials: 'same-origin' })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          var itemsEl = document.getElementById('drawer-items');
          var footEl = document.getElementById('drawer-foot');
          if (!itemsEl) return cart;
          var fhicLines = (cart && cart.items ? cart.items : []).filter(function (it) { return it.handle !== 'shipping-protection'; });
          if (!cart || !cart.items || fhicLines.length === 0) {
            itemsEl.innerHTML =
              '<div class="fh-drawer__empty" id="drawer-empty">' +
                '<div class="fh-drawer__empty__msg">Your bag is empty.</div>' +
                '<a href="/collections/shop-all" class="fh-drawer__empty__cta">Shop the drop &rarr;</a>' +
              '</div>';
            if (footEl) footEl.hidden = true;
          } else {
            if (footEl) footEl.hidden = false;
            itemsEl.innerHTML = fhicLines.map(function (item) {
              // `!= null`, not `||`: a line a discount makes free (BUY ONE GET ONE FREE splits
              // the cart into a $120 line and a $0 line) has final_price 0, and `0 || price`
              // fell back to the full price, so the drawer showed $240 for a $120 cart. (2026-10-10)
              var priceDollars = (item.final_price != null ? item.final_price : item.price) / 100;
              var lineTotalDollars = (item.final_line_price != null ? item.final_line_price : item.line_price) / 100;
              var variantStr = (item.variant_options || []).filter(Boolean).join(' / ') || item.variant_title || '';
              var imgSrc = item.image || item.featured_image && item.featured_image.url || '';
              // Match .fh-drawer__item__img aspect (4:5) via Shopify CDN crop
              // so every drawer thumbnail is the same shape regardless of the
              // source image's native aspect ratio. Strip any existing width/
              // height/crop from Shopify's returned URL — /cart.js sometimes
              // ships a pre-sized URL that fights our target dimensions.
              if (imgSrc) {
                imgSrc = imgSrc.replace(/[?&](width|height|crop)=[^&]*/g, '');
                var sep = imgSrc.indexOf('?') === -1 ? '?' : '&';
                imgSrc = imgSrc + sep + 'width=200&height=250&crop=center';
              }
              var qty = parseInt(item.quantity, 10) || 0;
              return ''
                + '<div class="fh-drawer__item" data-key="' + esc(item.key) + '" data-variant-id="' + esc(item.variant_id) + '" data-handle="' + esc(item.handle) + '"'
                +   ((FHIC_ADDON[item.handle] || item.product_type === 'Insurance') ? ' data-addon="1"' : '') + '>'
                +   '<div class="fh-drawer__item__img"><img src="' + esc(imgSrc) + '" alt="" loading="lazy"></div>'
                +   '<div>'
                +     '<div class="fh-drawer__item__title">' + esc(item.product_title || item.title) + '</div>'
                +     '<div class="fh-drawer__item__var">' + esc(variantStr) + '</div>'
                +     '<div class="fh-drawer__item__row">'
                +       '<div class="fh-drawer__qty" data-price="' + priceDollars + '">'
                +         '<button class="qty-down" type="button" aria-label="Decrease quantity">&minus;</button>'
                +         '<input value="' + qty + '" readonly aria-label="Quantity">'
                +         '<button class="qty-up" type="button" aria-label="Increase quantity">+</button>'
                +       '</div>'
                +       '<div class="mono line-total">' + fmt(lineTotalDollars) + '</div>'
                +     '</div>'
                +     '<button class="fh-drawer__remove" type="button">Remove</button>'
                +   '</div>'
                + '</div>';
            }).join('');
            itemsEl.querySelectorAll('.fh-drawer__item').forEach(bindDrawerItem);
          }
          if (typeof window.fhicSync === 'function') {
            try { window.fhicSync(cart); } catch (e) { console.warn('[fh-cart] fhicSync failed', e); }
          }
          recalcDrawer();
          return cart;
        })
        .catch(function (e) { console.warn('[fh-cart] refresh failed', e); });
    }

    function addToShopifyCart(variantId, quantity) {
      return fetch(CART_ADD_URL, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity: quantity || 1 }] })
      }).then(function (r) {
        if (!r.ok) return r.json().then(function (err) { throw err; });
        return r.json();
      });
    }
    window.fhAddToCart = addToShopifyCart;
    window.fhRefreshDrawer = refreshDrawerFromShopifyCart;

    // REMOVED 2026-08-17: addDummyLine(). It painted a drawer row that did not
    // exist server-side whenever variant resolution failed, so the shopper saw a
    // full bag and arrived at an EMPTY checkout — silent lost revenue on 51% of
    // card colour x size combinations. Every failure path now surfaces a visible
    // error or routes to the PDP. Do not reintroduce a client-only cart line.

    // Fetch a product's real Shopify variant + options data from
    // /products/{handle}.js. Returns a promise that resolves to
    // {variants, colorOptionPosition, sizeOptionPosition, sizes} or rejects.
    // Cached per-handle so repeat card clicks don't re-fetch.
    var _productCache = {};
    function fetchProductVariants(handle) {
      if (!handle) return Promise.reject(new Error('no handle'));
      if (_productCache[handle]) return Promise.resolve(_productCache[handle]);
      return fetch('/products/' + handle + '.js', { credentials: 'same-origin' })
        .then(function (r) { return r.ok ? r.json() : Promise.reject(r); })
        .then(function (data) {
          var colorPos = 0, sizePos = 0;
          var opts = data.options || [];
          opts.forEach(function (o, i) {
            var name = (typeof o === 'string') ? o : (o.name || '');
            if (/^colou?r$/i.test(name)) {
              colorPos = i + 1;
            } else if (sizePos === 0) {
              sizePos = i + 1;
            }
          });
          var sizes = [];
          opts.forEach(function (o, i) {
            var name = (typeof o === 'string') ? o : (o.name || '');
            if ((i + 1) === sizePos) {
              sizes = (typeof o === 'string') ? [] : (o.values || []);
            }
          });
          var enriched = {
            variants: data.variants || [],
            colorOptionPosition: colorPos,
            sizeOptionPosition: sizePos,
            sizes: sizes
          };
          _productCache[handle] = enriched;
          return enriched;
        });
    }

    // ===== Size picker (used by PDP main-ATC fallback + card ATC on homepage/collection) =====
    var sizePickerOverlay = document.getElementById('size-picker-overlay');
    var sizePicker = document.getElementById('size-picker');
    var sizePickerClose = document.getElementById('size-picker-close');
    var sizePickerPills = document.getElementById('size-picker-pills');
    var sizePickerImg = document.getElementById('size-picker-img');
    var sizePickerProductTitle = document.getElementById('size-picker-product-title');
    var sizePickerProductPrice = document.getElementById('size-picker-product-price');
    var pendingAdd = null;

    // openSizePicker: entry point for size-select flows.
    //   product: { title, price, img, color, size, variantId?, productHandle?, variants?, options?, colorOptionPosition?, sizeOptionPosition? }
    //   card: reserved (was HTMLElement for the removed cart-upsell flow — kept in signature so PDP + card ATC callers stay identical; pass null)
    //   sizes: [strings] to rebuild pills; defaults to S/M/L/XL
    //
    // If product.variants is present (Shopify variant data), commit does a real
    // /cart/add.js POST + drawer refresh. Otherwise the dummy insert (legacy
    // card path pending Phase 4 rewire).
    // ===== Real-variant resolution (2026-08-17) ==============================
    // The cards' hardcoded data-sizes / data-color had drifted from Shopify's
    // actual option values ("S" vs "small-1", "Grey" vs "gray"), so 100 of 197
    // colour x size combinations (51%, 25 cards) resolved to NO variant and fell
    // through to a phantom drawer row with no /cart/add.js — the shopper saw a
    // full bag and reached an EMPTY checkout. Truth now comes from the product
    // JSON; data-sizes / data-color are first-paint hints only.
    var COLOR_SYNONYMS = { grey: 'gray', gray: 'grey', creme: 'beige', cream: 'beige' };
    function normColor(s) {
      // Fold diacritics FIRST. Stripping non-alphanumerics before folding turns
      // "Crème" into "crme", which then misses the creme->beige synonym and sent
      // a real colour to the PDP fallback.
      var t = normOpt(s);
      if (t.normalize) t = t.normalize('NFD').replace(/[̀-ͯ]/g, '');
      return t.replace(/\s*&\s*/g, ' ').replace(/[^a-z0-9 ]+/g, '').trim();
    }
    function distinctOptionValues(variants, pos) {
      var seen = [];
      variants.forEach(function (v) {
        var val = v['option' + pos];
        if (val != null && seen.indexOf(val) === -1) seen.push(val);
      });
      return seen;
    }
    // Resolve the card's DISPLAY colour to exactly ONE real option value.
    // FAILS CLOSED: ambiguous or unmatched returns ok:false and the caller sends
    // the shopper to the PDP. Never treat an unmatched colour as "unconstrained" —
    // that would sell mint to someone who clicked Grey.
    function canonicalColor(variants, colorPos, want) {
      if (!colorPos) return { ok: true, value: null };   // product has no colour axis
      var w = normColor(want);
      if (!w) return { ok: false, value: null };
      var hits = distinctOptionValues(variants, colorPos).filter(function (v) {
        var n = normColor(v);
        return n === w || COLOR_SYNONYMS[w] === n || COLOR_SYNONYMS[n] === w;
      });
      return hits.length === 1 ? { ok: true, value: hits[0] } : { ok: false, value: null };
    }
    // Sizes that really exist for the chosen colour. Raw values preserved so
    // resolveVariantId matches exactly; availability drives the disabled state.
    function sizeRowsFor(variants, colorPos, sizePos, canonColor) {
      var rows = [];
      variants.forEach(function (v) {
        if (colorPos && canonColor != null &&
            normOpt(v['option' + colorPos]) !== normOpt(canonColor)) return;
        var raw = sizePos ? v['option' + sizePos] : null;
        if (raw == null) return;
        for (var i = 0; i < rows.length; i++) {
          if (rows[i].raw === raw) { rows[i].available = rows[i].available || !!v.available; return; }
        }
        rows.push({ raw: raw, available: !!v.available });
      });
      return rows;
    }
    // Collision-aware labels: blindly stripping a trailing -N would render raw
    // "small" and "small-1" as two identical "SMALL" pills for DIFFERENT variants.
    function labelSizeRows(rows) {
      var counts = {};
      rows.forEach(function (r) {
        var base = String(r.raw).replace(/-\d+$/, '');
        counts[base] = (counts[base] || 0) + 1;
      });
      rows.forEach(function (r) {
        var base = String(r.raw).replace(/-\d+$/, '');
        r.label = counts[base] > 1 ? String(r.raw) : base;
      });
      return rows;
    }
    function goToPdp(product) {
      if (product && product.productHandle) {
        window.location.href = '/products/' + product.productHandle;
        return true;
      }
      return false;
    }
    function pickerMessage(msg) {
      if (sizePickerPills) {
        sizePickerPills.innerHTML = '<p class="fh-size-picker__msg" role="status">' + esc(msg) + '</p>';
      }
    }

    function showPickerShell(product) {
      if (sizePickerImg) sizePickerImg.src = product.img || '';
      if (sizePickerProductTitle) sizePickerProductTitle.textContent = product.title || '';
      if (sizePickerProductPrice) sizePickerProductPrice.textContent = fmt(product.price || 0);
      if (document.activeElement) _lastOpener.push(document.activeElement);
      if (sizePickerOverlay) sizePickerOverlay.classList.add('is-open');
      if (sizePicker) {
        sizePicker.classList.add('is-open');
        sizePicker.setAttribute('aria-hidden', 'false');
      }
      lockBody();
      setTimeout(function () { moveFocusInto(sizePicker); }, 40);
    }

    // Render pills from REAL variant data. Bails to the PDP rather than guessing.
    function fillPicker(product, data) {
      var variants = data.variants || [];
      var colorPos = data.colorOptionPosition || 0;
      var sizePos  = data.sizeOptionPosition  || 0;

      // Third option axis: resolution below only constrains colour + size, so an
      // arbitrary third-axis value would be picked silently. Bail out instead.
      var axisCount = 0;
      variants.forEach(function (v) {
        if (v.option3 != null) axisCount = Math.max(axisCount, 3);
        else if (v.option2 != null) axisCount = Math.max(axisCount, 2);
        else axisCount = Math.max(axisCount, 1);
      });
      if (axisCount >= 3 && colorPos && sizePos) {
        closeSizePicker(); if (!goToPdp(product)) pickerMessage('Please choose options on the product page.');
        return;
      }

      var canon = canonicalColor(variants, colorPos, product.color);
      if (!canon.ok) {
        // Unmappable colour — adding anything here risks shipping the wrong item.
        closeSizePicker();
        if (!goToPdp(product)) pickerMessage('Please choose a colour on the product page.');
        return;
      }
      product.canonicalColor = canon.value;

      var rows = labelSizeRows(sizeRowsFor(variants, colorPos, sizePos, canon.value));

      // No genuine size axis (single variant / "Default Title" / colour-only):
      // skip the picker entirely and add the uniquely resolved available variant.
      if (!sizePos || !rows.length) {
        var only = null;
        for (var i = 0; i < variants.length; i++) {
          var v = variants[i];
          if (colorPos && canon.value != null &&
              normOpt(v['option' + colorPos]) !== normOpt(canon.value)) continue;
          if (v.available) { only = v; break; }
        }
        closeSizePicker();
        if (!only) { goToPdp(product); return; }
        addToShopifyCart(only.id, 1)
          .then(function () { return refreshDrawerFromShopifyCart(); })
          .then(openDrawer)
          .catch(function () { goToPdp(product); });
        return;
      }

      if (sizePickerPills) {
        sizePickerPills.innerHTML = rows.map(function (r) {
          // data-size keeps the RAW value so resolveVariantId matches Shopify's
          // exact option string; the label is the collision-aware display form.
          return '<button type="button" class="fh-size-picker__pill"' +
                 (r.available ? '' : ' disabled aria-disabled="true"') +
                 ' data-size="' + esc(r.raw) + '">' + esc(r.label) + '</button>';
        }).join('');
      }
    }

    function openSizePicker(product, card, sizes) {
      pendingAdd = { product: product };
      showPickerShell(product);

      // Variant truth, in priority order. openSizePicker is shared by the card
      // ATC, the PDP sticky ATC and modal upsells — the latter two already pass a
      // populated product.variants and have NO variantsPromise, so never block on
      // a promise that does not exist.
      if (product.variants && product.variants.length) {
        fillPicker(product, product);
        return;
      }
      if (product.variantsPromise) {
        pickerMessage('Loading sizes…');
        product.variantsPromise
          .then(function (enriched) {
            if (!pendingAdd || pendingAdd.product !== product) return;  // picker moved on
            if (!enriched || !enriched.variants || !enriched.variants.length) {
              closeSizePicker(); goToPdp(product); return;
            }
            product.variants = enriched.variants;
            product.colorOptionPosition = enriched.colorOptionPosition;
            product.sizeOptionPosition = enriched.sizeOptionPosition;
            fillPicker(product, enriched);
          })
          .catch(function () { closeSizePicker(); goToPdp(product); });
        return;
      }
      // No variants and no way to fetch them — we cannot honestly add anything.
      closeSizePicker();
      goToPdp(product);
    }
    function closeSizePicker() {
      if (sizePickerOverlay) sizePickerOverlay.classList.remove('is-open');
      if (sizePicker) {
        sizePicker.classList.remove('is-open');
        sizePicker.setAttribute('aria-hidden', 'true');
      }
      // Clear pendingAdd fully so a stale variantsPromise from a previously
      // opened card can't merge its variants into the next card's product on
      // resolve. Race guard for slow networks + fast-clicking users.
      pendingAdd = null;
      unlockBody();
      returnFocus();
    }
    window.openSizePicker = openSizePicker;
    if (sizePickerClose) sizePickerClose.addEventListener('click', closeSizePicker);
    if (sizePickerOverlay) sizePickerOverlay.addEventListener('click', closeSizePicker);

    // Look up a Shopify variant ID from the product payload by matching color +
    // size. Comparison is case-insensitive — cards may render "Black" / "M"
    // (title case) while Shopify's option axes are often authored lowercase
    // ("black" / "m") or with dedupe suffixes ("small-1"). Prefer only-
    // available variants; fall back to any match so out-of-stock detection
    // happens server-side in /cart/add.js.
    function normOpt(s) { return s == null ? '' : String(s).toLowerCase().trim(); }
    // availableOnly: on the card path we must NOT fall back to an unavailable
    // variant — that produced a 422 which then fell through to a phantom line.
    function resolveVariantId(product, chosenSize, availableOnly) {
      if (!product.variants || !product.variants.length) return null;
      var colorPos = product.colorOptionPosition; // 1, 2, or 3 (0 = no color axis)
      var sizePos  = product.sizeOptionPosition;
      var wantColor = normOpt(product.color);
      var wantSize  = normOpt(chosenSize);
      var fallback = null;
      for (var i = 0; i < product.variants.length; i++) {
        var v = product.variants[i];
        var colorOk = !colorPos || !wantColor || normOpt(v['option' + colorPos]) === wantColor;
        var sizeOk  = !sizePos  || !wantSize  || normOpt(v['option' + sizePos])  === wantSize;
        if (colorOk && sizeOk) {
          if (v.available) return v.id;
          if (fallback === null) fallback = v.id;
        }
      }
      return availableOnly ? null : fallback;
    }

    // Event delegation on the pills container.
    if (sizePickerPills) {
      sizePickerPills.addEventListener('click', function (e) {
        var pill = e.target.closest('.fh-size-picker__pill');
        if (!pill || !pendingAdd) return;
        if (pill.disabled || pendingAdd.committing) return;   // latch: exactly one POST
        pendingAdd.committing = true;
        pill.classList.add('is-committing');
        var size = pill.dataset.size;
        var product = pendingAdd.product;

        function afterCommit() {
          openDrawer();
          setTimeout(closeSizePicker, 120);
        }
        function failCommit(msg) {
          if (pendingAdd) pendingAdd.committing = false;
          pill.classList.remove('is-committing');
          // NEVER paint a drawer row the server has not acknowledged. The old
          // addDummyLine() fallback is what turned every resolution failure into
          // a silent empty checkout; a visible error is strictly better.
          pickerMessage(msg || 'Sorry — that size is unavailable. Try another.');
        }

        function commitWithVariants() {
          // Resolve against the CANONICAL colour worked out in fillPicker, not the
          // card's display colour, and only ever to an AVAILABLE variant.
          var variantId = resolveVariantId(
            { variants: product.variants,
              colorOptionPosition: product.colorOptionPosition,
              sizeOptionPosition: product.sizeOptionPosition,
              color: product.canonicalColor != null ? product.canonicalColor : product.color },
            size,
            true /* availableOnly */
          );
          if (!variantId) { failCommit('Sorry — that size just sold out.'); return Promise.resolve(); }
          return addToShopifyCart(variantId, 1)
            .then(function () { return refreshDrawerFromShopifyCart(); })
            .then(afterCommit)
            .catch(function (err) {
              console.warn('[fh-cart] add failed', err);
              failCommit('Sorry — we could not add that. Please try again.');
            });
        }

        // No promise-waiting needed any more: fillPicker() only renders pills AFTER
        // real variant data has resolved, so a clickable pill implies loaded
        // variants. If they are somehow absent, fail visibly — never a dummy line.
        if (!product.variants || !product.variants.length) {
          failCommit('Sorry — we could not load that product. Please try again.');
        } else {
          commitWithVariants();
        }
      });
    }

    // ===== Product card interactions (used on homepage + collection) =====
    function inferColorFromTitle(title) {
      if (/cr[èe]me/i.test(title)) return 'Crème';
      if (/grey|gray/i.test(title))  return 'Grey';
      if (/brown/i.test(title))      return 'Brown';
      if (/red/i.test(title))        return 'Red';
      if (/white/i.test(title))      return 'White';
      if (/blue/i.test(title))       return 'Blue';
      return 'Black';
    }
    function getCardPrice(card) {
      if (card.dataset.price) return parseFloat(card.dataset.price);
      var priceEl = card.querySelector('.fh-card__price');
      var text = priceEl ? priceEl.textContent : '';
      var match = text.match(/\$([\d.,]+)/);
      return match ? parseFloat(match[1].replace(/,/g, '')) : 0;
    }
    document.querySelectorAll('.fh-card').forEach(function (card) {
      var titleEl = card.querySelector('.fh-card__title');
      var title = titleEl ? titleEl.textContent.trim() : 'Product';
      var activeSwatch = card.querySelector('.fh-card__swatch.is-active') || card.querySelector('.fh-card__swatch');
      if (!card.dataset.color) {
        card.dataset.color = (activeSwatch && activeSwatch.dataset.color) || inferColorFromTitle(title);
      }
      var defaultSize = card.querySelector('.fh-card__size.is-selected')
                     || Array.prototype.slice.call(card.querySelectorAll('.fh-card__size')).find(function (b) { return !b.disabled; });
      card.dataset.size = (defaultSize && defaultSize.textContent.trim()) || 'OS';

      card.querySelectorAll('.fh-card__swatch').forEach(function (sw) {
        sw.addEventListener('click', function (e) {
          e.preventDefault(); e.stopPropagation();
          card.querySelectorAll('.fh-card__swatch').forEach(function (s) { s.classList.toggle('is-active', s === sw); });
          if (sw.dataset.color) card.dataset.color = sw.dataset.color;
          if (sw.dataset.img) {
            var img = card.querySelector('.fh-card__media img');
            if (img) img.src = sw.dataset.img;
          }
        });
      });

      card.querySelectorAll('.fh-card__size').forEach(function (pill) {
        pill.addEventListener('click', function (e) {
          if (pill.disabled) return;
          e.preventDefault(); e.stopPropagation();
          card.querySelectorAll('.fh-card__size').forEach(function (p) { p.classList.toggle('is-selected', p === pill); });
          card.dataset.size = pill.textContent.trim();
        });
      });

      // Try to derive the product handle from the card's <a class="fh-card__media">
      // href so we can hit /products/{handle}.js for real variant data on ATC.
      var mediaLink = card.querySelector('a.fh-card__media');
      var cardHandle = null;
      if (mediaLink) {
        var href = mediaLink.getAttribute('href') || '';
        var m = href.match(/\/products\/([a-z0-9-]+)/i);
        if (m) cardHandle = m[1];
      }

      var cta = card.querySelector('.fh-card__cta');
      if (cta && !cta.disabled) {
        cta.addEventListener('click', function (e) {
          e.preventDefault(); e.stopPropagation();
          var img = card.querySelector('.fh-card__media img');
          var product = {
            title: title,
            price: getCardPrice(card),
            img:   img ? img.src : '',
            color: card.dataset.color || 'Black',
            size:  card.dataset.size  || 'OS',
            productHandle: cardHandle
          };
          var cardSizes = (card.dataset.sizes || '')
            .split(',').map(function (s) { return s.trim(); }).filter(Boolean);
          if (!cardSizes.length) {
            cardSizes = Array.prototype.slice
              .call(card.querySelectorAll('.fh-card__size'))
              .filter(function (b) { return !b.disabled; })
              .map(function (b) { return b.textContent.trim(); });
          }
          // Kick off the /products/{handle}.js fetch in parallel with picker
          // open so by the time the shopper picks a size the variants are
          // (usually) already loaded and the real /cart/add.js POST fires
          // instead of a dummy line.
          if (cardHandle) {
            product.variantsPromise = fetchProductVariants(cardHandle);
          }
          openSizePicker(product, null, cardSizes);
        });
      }
    });
  });
})();


/* V23 mobile header reliability override.
   Some iOS Safari/Shopify combinations can make a sticky element appear to
   leave the viewport. Fixed positioning on mobile makes the reveal behavior
   deterministic; desktop keeps the existing sticky implementation. */
(function () {
  var header = document.querySelector('.fh-header');
  if (!header) return;
  var lastY = window.pageYOffset || 0;
  var ticking = false;
  function isMobile() { return window.matchMedia('(max-width: 720px)').matches; }
  function update() {
    var y = window.pageYOffset || document.documentElement.scrollTop || 0;
    if (!isMobile()) {
      header.classList.remove('fh-header--hidden');
      lastY = y;
      ticking = false;
      return;
    }
    var delta = y - lastY;
    if (y <= 4 || delta < -2) {
      header.classList.remove('fh-header--hidden');
    } else if (delta > 2) {
      header.classList.add('fh-header--hidden');
    }
    lastY = y;
    ticking = false;
  }
  window.addEventListener('scroll', function () {
    if (!ticking) {
      window.requestAnimationFrame(update);
      ticking = true;
    }
  }, { passive: true });
  window.addEventListener('resize', update, { passive: true });
  update();
})();

/* V31 — dynamically protect the centered mobile wordmark from side controls.
   Measure the actual rendered elements rather than guessing from viewport
   width, so the logo only gets smaller when it genuinely needs to. */
(function () {
  function fitMobileWordmark() {
    var header = document.querySelector('.fh-header--editorial');
    if (!header) return;
    var wordmark = header.querySelector('.fh-wordmark');
    var toggle = header.querySelector('.fh-nav-toggle');
    var utility = header.querySelector('.fh-utility');
    if (!wordmark || !toggle || !utility) return;

    if (!window.matchMedia('(max-width: 720px)').matches) {
      wordmark.style.fontSize = '';
      return;
    }

    var link = wordmark.querySelector('a') || wordmark;
    var maxFont = 23;
    var minFont = 13;
    var safeGap = 6;

    wordmark.style.fontSize = maxFont + 'px';

    /* Reduce in small increments until both sides have the requested gap. */
    for (var size = maxFont; size >= minFont; size -= 0.25) {
      wordmark.style.fontSize = size + 'px';
      var logo = wordmark.getBoundingClientRect();
      var left = toggle.getBoundingClientRect();
      var right = utility.getBoundingClientRect();
      var leftClearance = logo.left - left.right;
      var rightClearance = right.left - logo.right;
      if (leftClearance >= safeGap && rightClearance >= safeGap) break;
    }
  }

  var fitTimer;
  function scheduleFit() {
    window.clearTimeout(fitTimer);
    fitTimer = window.setTimeout(fitMobileWordmark, 30);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleFit);
  } else {
    scheduleFit();
  }
  window.addEventListener('resize', scheduleFit, { passive: true });
  window.addEventListener('orientationchange', scheduleFit, { passive: true });
})();
